import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Save } from "lucide-react";
import FileUploader from "./FileUploader";

const empty = { label: "", category: "Annual Reports", url: "", description: "", order: 100 };

export default function AdminDocuments() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const load = useCallback(() => api.get("/documents").then((r) => setItems(r.data || [])), []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            const payload = { ...form, order: Number(form.order) || 100 };
            if (form.id) await api.put(`/documents/${form.id}`, payload);
            else await api.post("/documents", payload);
            toast.success("Saved");
            setForm(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete document?")) return;
        await api.delete(`/documents/${id}`);
        load();
    };

    return (
        <div data-testid="admin-documents">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold">Documents</h1>
                    <p className="text-sm text-slate-500 mt-1">Annual reports, financials, registrations.</p>
                </div>
                <button onClick={() => setForm({ ...empty })} data-testid="document-add-btn" className="px-4 py-2 rounded-md bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={14} /> Add document</button>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-5 py-3">Label</th>
                            <th className="text-left px-5 py-3">Category</th>
                            <th className="text-left px-5 py-3">URL</th>
                            <th className="text-right px-5 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((d) => (
                            <tr key={d.id} className="border-t border-slate-100">
                                <td className="px-5 py-3">{d.label}</td>
                                <td className="px-5 py-3 text-slate-500">{d.category}</td>
                                <td className="px-5 py-3 text-slate-500 truncate max-w-xs">{d.url}</td>
                                <td className="px-5 py-3 text-right">
                                    <button onClick={() => setForm({ ...d })} className="p-2 hover:bg-slate-100 rounded"><Pencil size={14} /></button>
                                    <button onClick={() => remove(d.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {form && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setForm(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-lg w-full">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200"><h2 className="font-semibold">{form.id ? "Edit document" : "Add document"}</h2><button onClick={() => setForm(null)}><X size={18} /></button></div>
                        <div className="p-5 space-y-3">
                            <input placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <input placeholder="Category (e.g. Annual Reports)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <textarea rows="2" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                            <FileUploader kind="document" current={null} label="Upload PDF" onUploaded={(r) => setForm({ ...form, url: r.url })} />
                            <input placeholder="Or external URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                        </div>
                        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
                            <button onClick={() => setForm(null)} className="px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={save} className="px-4 py-2 rounded bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
